import { NativeSelect } from "@/components/ui/native-select";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import {
  type TaskInvestigationQueueRow,
  useTaskInvestigations,
  useCarrierTrips,
  useResolveTaskInvestigation,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";

function InvestigationRow({
  record,
  readOnly,
}: {
  record: TaskInvestigationQueueRow;
  readOnly: boolean;
}) {
  const resolve = useResolveTaskInvestigation();
  const [resolution, setResolution] = useState<"RECOVERED" | "CONFIRMED_LOST">(
    "RECOVERED"
  );
  const [notes, setNotes] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState("");
  const cancelled = record.task.status === "CANCELLED";
  const canResume = !cancelled && record.sourceTrip.status === "ACTIVE";
  const [recoveryPage, setRecoveryPage] = useState(1);
  const recoveryTrips = useCarrierTrips({
    status: "PLANNED",
    dispatchCenterId: record.sourceTrip.dispatchCenterId,
    page: recoveryPage,
    limit: 20,
    enabled: resolution === "RECOVERED" && !cancelled,
  });
  const resuming = canResume && target === record.sourceTripId;
  const recoveryReadBlocked =
    recoveryTrips.isError ||
    recoveryTrips.isFetching ||
    recoveryTrips.isPlaceholderData ||
    !recoveryTrips.data;
  const planned = recoveryReadBlocked ? [] : recoveryTrips.data.data;
  const destinationReady =
    resuming || planned.some((trip) => trip.id === target);
  const changeRecoveryPage = (page: number) => {
    setTarget("");
    setRecoveryPage(page);
  };
  return (
    <article className="mt-3 rounded-md border bg-background p-3 text-sm">
      <p className="font-medium">
        Task #{record.task.taskNumber} ·{" "}
        {record.state === "CONFIRMED_LOST"
          ? "Confirmed lost — delivery blocked"
          : "Needs investigation"}{" "}
        · source Trip #{record.sourceTrip.tripNumber} (
        {record.sourceTrip.status.toLowerCase()})
      </p>
      {record.reportNotes ? <p>Driver report: {record.reportNotes}</p> : null}
      {record.resolutionNotes ? (
        <p className="whitespace-pre-wrap break-words">
          <strong>Latest dispatch findings:</strong> {record.resolutionNotes}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2">
        <NativeSelect
          aria-label={`Resolution for Task ${record.task.taskNumber}`}
          value={resolution}
          onChange={(event) =>
            setResolution(
              event.target.value === "CONFIRMED_LOST"
                ? "CONFIRMED_LOST"
                : "RECOVERED"
            )
          }
          disabled={readOnly || resolve.isPending}
        >
          <option value="RECOVERED">
            {cancelled
              ? "Found — record finding; Task stays cancelled"
              : "Found — resume or replan the same Task"}
          </option>
          <option value="CONFIRMED_LOST">
            Confirmed lost — delivery stays stopped
          </option>
        </NativeSelect>
        {resolution === "RECOVERED" && !cancelled ? (
          <NativeSelect
            aria-label={`Recovery Trip for Task ${record.task.taskNumber}`}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            disabled={readOnly || resolve.isPending}
          >
            <option value="">Select a recovery destination</option>
            {canResume ? (
              <option value={record.sourceTripId}>
                Verified with original driver — resume current Trip
              </option>
            ) : null}
            {planned.map((trip) => (
              <option key={trip.id} value={trip.id}>
                Trip #{trip.tripNumber}
              </option>
            ))}
          </NativeSelect>
        ) : null}
        {resolution === "RECOVERED" && !cancelled ? (
          <div className="w-full text-xs text-muted-foreground">
            {recoveryTrips.isError ? (
              <p role="alert">
                Could not load planned recovery Trips.{" "}
                <Button
                  type="button"
                  size="sm-touch"
                  onClick={() => void recoveryTrips.refetch()}
                >
                  Refresh recovery Trips
                </Button>
              </p>
            ) : recoveryReadBlocked ? (
              <p>Loading planned recovery Trips…</p>
            ) : planned.length === 0 ? (
              <p>
                No planned recovery Trips on this page in the investigation's
                center.
              </p>
            ) : null}
            {target && !destinationReady && !recoveryReadBlocked ? (
              <p>
                Selected Trip is no longer available. Choose a current recovery
                destination.
              </p>
            ) : null}
            {recoveryPage > 1 ||
            (!recoveryReadBlocked &&
              (recoveryTrips.data?.metadata.totalPages ?? 0) > 1) ? (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  type="button"
                  size="sm-touch"
                  aria-label={`Previous recovery Trips for Task ${record.task.taskNumber}`}
                  disabled={
                    readOnly ||
                    resolve.isPending ||
                    recoveryTrips.isFetching ||
                    recoveryPage === 1
                  }
                  onClick={() => changeRecoveryPage(recoveryPage - 1)}
                >
                  Previous recovery Trips
                </Button>
                <span>Page {recoveryPage}</span>
                <Button
                  type="button"
                  size="sm-touch"
                  aria-label={`Next recovery Trips for Task ${record.task.taskNumber}`}
                  disabled={
                    readOnly ||
                    resolve.isPending ||
                    recoveryReadBlocked ||
                    recoveryPage >=
                      (recoveryTrips.data?.metadata.totalPages ?? 0)
                  }
                  onClick={() => changeRecoveryPage(recoveryPage + 1)}
                >
                  Next recovery Trips
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        <Input
          aria-label={`Findings for Task ${record.task.taskNumber}`}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Record verified investigation findings"
          disabled={readOnly || resolve.isPending}
        />
        <Button
          size="sm-touch"
          disabled={
            readOnly ||
            resolve.isPending ||
            !notes.trim() ||
            (resolution === "RECOVERED" && !cancelled && !destinationReady)
          }
          onClick={async () => {
            setError("");
            try {
              await resolve.mutateAsync({
                id: record.id,
                resolution,
                expectedReportedAt: record.reportedAt,
                expectedResolvedAt: record.resolvedAt ?? null,
                notes: notes.trim(),
                ...(resolution === "RECOVERED" &&
                !cancelled &&
                target &&
                !resuming
                  ? { recoveryTripId: target }
                  : {}),
              });
            } catch {
              setError(
                "Resolution unconfirmed. Refresh current investigation before another decision."
              );
            }
          }}
        >
          Confirm resolution
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {cancelled
          ? "Record the physical finding only. This Task stays cancelled; investigation cannot resume or replan cancelled work."
          : resolution === "RECOVERED"
            ? "Confirm the parcel was found. Replanning preserves the original Trip/start history and requires actual collection before departure; it creates no retry Task or receipt."
            : "Record the confirmed physical loss. Delivery stays blocked; this does not complete/fail the Task, create a retry, transfer custody or settle liability/refunds."}
      </p>
      {error ? <p role="alert">{error}</p> : null}
    </article>
  );
}

/** Persistent scoped queue does not follow route/date/window filters. */
export function TaskInvestigations() {
  const [page, setPage] = useState(1);
  const query = useTaskInvestigations(page);
  if (!query.data && !query.isError) return null;
  if (!query.data?.total && !query.isError) return null;
  return (
    <section
      aria-label="Started-loss investigations"
      className="max-h-80 shrink-0 overflow-y-auto border-b border-amber-300 bg-amber-50 px-4 py-3"
    >
      <h2 className="text-sm font-semibold">
        Needs investigation — started loss ({query.data?.total ?? "unavailable"}
        )
      </h2>
      <p className="text-xs">
        The driver can finish other work and explicitly close the Trip while
        dispatch investigates. Confirmed loss stays visible and may be corrected
        by later verified recovery; it is not a failed attempt or retry.
      </p>
      {query.isError ? (
        <p role="alert">
          Could not confirm current investigations.{" "}
          <Button type="button" onClick={() => void query.refetch()}>
            Refresh investigations
          </Button>
        </p>
      ) : null}
      {query.data?.data.map((record) => (
        <InvestigationRow
          key={`${record.id}:${record.resolvedAt ?? "open"}`}
          record={record}
          readOnly={
            query.isError || query.isFetching || query.isPlaceholderData
          }
        />
      ))}
      <div className="mt-2 flex gap-2">
        <Button
          size="sm-touch"
          disabled={page === 1 || query.isFetching}
          onClick={() => setPage((current) => current - 1)}
        >
          Previous investigations
        </Button>
        <Button
          size="sm-touch"
          disabled={
            !query.data || page * 20 >= query.data.total || query.isFetching
          }
          onClick={() => setPage((current) => current + 1)}
        >
          Next investigations
        </Button>
      </div>
    </section>
  );
}
